/**
 * cas-2c1b (was cas-bdca's /go script): a sign-in link is the real page with
 * the token in the `k` query parameter, e.g. /audit/<id>?s=<share>&k=<token>.
 *
 * This script is inlined into <head> (nuxt.config.ts app.head.script) so it
 * runs before ANY bundle: before vue-router reads the initial URL and before
 * posthog-js initialises. posthog-js records the initial URL into person
 * properties at init and sends them on /flags requests, which never pass
 * through before_send, so a Nuxt plugin would run too late.
 *
 * On any path, it parks `k` — bound to the page's pathname — in this tab's
 * sessionStorage and rewrites the
 * address WITHOUT `k`. The link-sign-in.global middleware reads the parked
 * token. If storage is unavailable, the URL is left alone and the middleware
 * falls back to the route query (before_send redaction still covers events).
 *
 * Plain ES5, no dependencies: it is shipped as a string.
 */
export const LINK_TOKEN_QUERY_PARAM = 'k';
export const LINK_TOKEN_STORAGE_KEY = 'gabber:link-token';

/** The minted token shape: 16 random bytes, base64url (SIGN_IN_LINK_TOKEN_PATTERN in the backend). */
export const MINTED_LINK_TOKEN_PATTERN = /^[A-Za-z0-9_-]{22}$/;

export function isMintedLinkToken(value: unknown): value is string {
  return typeof value === 'string' && MINTED_LINK_TOKEN_PATTERN.test(value);
}

export const LINK_LOADER_ID = 'gabber-link-loader';

export interface LinkLoaderCopy {
  title: string;
  body: string;
}

export interface LinkLoaderPresentation {
  html: string;
  css: string;
}

/**
 * The same script with a pre-mount loading screen (cas-2c1b QA F06): while
 * the link is checked on a client-only page, nothing of the app is on screen
 * yet — the route middleware holds the first navigation — so the script shows
 * "Opening your link" itself, as soon as the body exists and only when the
 * server rendered no page (an SSR page such as /audit/** stays visible).
 * middleware/link-sign-in.global.ts removes it when the check finishes.
 * `copy` is keyed by locale prefix ('' = default English, 'es', …).
 */
export function buildLinkTokenScript(
  copy: Record<string, LinkLoaderCopy>,
  presentation: LinkLoaderPresentation = {
    html: '<strong data-link-loader-title></strong><span data-link-loader-body></span>',
    css: ''
  }
): string {
  const esc = (text: string) => JSON.stringify(text).replace(/</g, '\\u003c');
  const copyJson = `{${Object.entries(copy)
    .map(([locale, strings]) => `${esc(locale)}:{t:${esc(strings.title)},b:${esc(strings.body)}}`)
    .join(',')}}`;
  return `(function(){try{
if(location.search.indexOf('${LINK_TOKEN_QUERY_PARAM}=')===-1)return;
var u=new URL(location.href);var t=u.searchParams.get('${LINK_TOKEN_QUERY_PARAM}');
if(!t)return;
var v=JSON.stringify({t:t,p:location.pathname});
sessionStorage.setItem('${LINK_TOKEN_STORAGE_KEY}',v);
if(sessionStorage.getItem('${LINK_TOKEN_STORAGE_KEY}')!==v)return;
u.searchParams.delete('${LINK_TOKEN_QUERY_PARAM}');
history.replaceState(history.state,'',u.pathname+u.search+u.hash);
var C=${copyJson};var m=/^\\/([a-z]{2})(?=\\/|$)/.exec(location.pathname);var s=(m&&C[m[1]])||C[''];
var light=true;try{light=localStorage.getItem('gabber-color-mode')!=='dark'}catch(e){}
var P={h:${esc(presentation.html)},c:${esc(presentation.css)}};
var show=function(){try{
var n=document.getElementById('__nuxt');if(n&&n.childElementCount>0)return;
if(document.getElementById('${LINK_LOADER_ID}'))return;
var d=document.createElement('div');d.id='${LINK_LOADER_ID}';d.className=light?'body--light':'body--dark';
d.innerHTML='<style>'+P.c+'</style>'+P.h;
if(!d.querySelector('[role="status"]')){d.setAttribute('role','status');d.setAttribute('aria-live','polite')}
d.querySelector('[data-link-loader-title]').textContent=s.t;d.querySelector('[data-link-loader-body]').textContent=s.b;
document.body.appendChild(d);
setTimeout(function(){var x=document.getElementById('${LINK_LOADER_ID}');if(x)x.remove()},45000);
}catch(e){}};
if(document.body)show();else document.addEventListener('DOMContentLoaded',show);
}catch(e){}})();`;
}

/** The script without loader copy (English only); tests and fallbacks use this. */
export const LINK_TOKEN_REDACTION_SCRIPT = buildLinkTokenScript({
  '': { title: 'Opening your link', body: 'Signing you in. This takes a moment.' }
});

/** A copy of `url` (path, query and hash) without the `k` parameter. Never throws. */
export function stripLinkTokenParam(url: string): string {
  const hashAt = url.indexOf('#');
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : url.slice(hashAt);
  const queryAt = beforeHash.indexOf('?');
  if (queryAt === -1) return url;
  const params = new URLSearchParams(beforeHash.slice(queryAt + 1));
  if (!params.has(LINK_TOKEN_QUERY_PARAM)) return url;
  params.delete(LINK_TOKEN_QUERY_PARAM);
  const query = params.toString();
  return `${beforeHash.slice(0, queryAt)}${query ? `?${query}` : ''}${hash}`;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Remove a sign-in link token from server-rendered HTML: every `k=<token>`
 * query pair in any encoding the renderer emits (`&`, `&amp;`, `\u0026`,
 * `?`), then any bare occurrence left over. The server renders with the
 * request URL as-is (h3 resets each layer's path, so a middleware cannot
 * rewrite it for the renderer); this runs on the finished HTML instead.
 */
export function scrubLinkTokenFromHtml(html: string, token: string): string {
  if (!isMintedLinkToken(token) || !html.includes(token)) return html;
  const t = escapeRegExp(token);
  const k = LINK_TOKEN_QUERY_PARAM;
  return (
    html
      // …&k=<token>, …&amp;k=<token>, …&k=<token> (not the first parameter)
      .replace(new RegExp(`(?:&amp;|&|\\\\u0026)${k}=${t}`, 'g'), '')
      // ?k=<token>&next=… → ?next=…   (first parameter, more follow)
      .replace(new RegExp(`\\?${k}=${t}(?:&amp;|&|\\\\u0026)`, 'g'), '?')
      // ?k=<token> (the only parameter)
      .replace(new RegExp(`\\?${k}=${t}`, 'g'), '')
      // Anything else that still carries the raw token.
      .replace(new RegExp(t, 'g'), 'redacted')
  );
}
